import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { format, differenceInDays, differenceInMinutes } from 'date-fns';
import { buildEmailLink, buildWhatsAppLink } from './AdminSettings';

const PLAN_BADGE  = { free:'badge-gray', trial:'badge-orange', plus:'badge-blue', family:'badge-purple' };
const PLAN_OPTS   = ['free', 'trial', 'plus', 'family'];
const STATUS_OPTS = ['active', 'suspended', 'cancelled'];

const EMPTY = {
  name: '', email: '', whatsapp: '', phone: '', plan: 'trial',
  device_name: '', status: 'active', notes: '', num_kids: 0,
  trial_ends_at: new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 16),
};

function TrialBadge({ customer }) {
  if (customer.plan !== 'trial' || !customer.trial_ends_at) return null;
  const days = differenceInDays(new Date(customer.trial_ends_at), new Date());
  if (days < 0) return <span className="badge badge-red">Trial expired</span>;
  if (days <= 3) return <span className="badge badge-red">{days}d left</span>;
  return <span className="badge badge-yellow">{days}d left</span>;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Action Modal — Reset Password / Change Email / Change Phone
   ═══════════════════════════════════════════════════════════════════════════ */
function ActionModal({ action, customer, onClose, onDone, showToast }) {
  const [value, setValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [resetDone, setResetDone] = useState(false); // show send buttons after reset

  const titles = {
    resetPassword: 'Reset Password',
    changeEmail: 'Change Email',
    changePhone: 'Change Phone',
    suspendAccount: 'Suspend Account',
    activateAccount: 'Activate Account',
    deleteAccount: 'Delete Account',
  };

  const handleSave = async () => {
    setError('');
    setSaving(true);

    try {
      if (action === 'resetPassword') {
        if (!value || value.length < 6) throw new Error('Password must be at least 6 characters');
        if (value !== confirm) throw new Error('Passwords do not match');
        if (!customer.auth_id) throw new Error('Customer has no auth_id — cannot reset password');

        // Use admin API to update password
        const { error: authErr } = await supabase.auth.admin.updateUserById(customer.auth_id, {
          password: value,
        });
        if (authErr) throw authErr;

        // Mark any pending password reset requests as completed
        await supabase.from('password_resets')
          .update({ status: 'completed', resolved_at: new Date().toISOString(), admin_notes: 'Password reset by admin' })
          .eq('email', customer.email)
          .eq('status', 'pending')
          .then(() => {}).catch(() => {}); // ignore if table doesn't exist yet

        showToast(`Password reset for ${customer.name}`);
        setResetDone(true);
        setSaving(false);
        return; // don't close modal — show send buttons
      }

      else if (action === 'changeEmail') {
        if (!value || !value.includes('@')) throw new Error('Enter a valid email');
        // Check duplicate
        const { data: dup } = await supabase.from('customers').select('id').eq('email', value.trim().toLowerCase()).limit(1);
        if (dup?.length > 0) throw new Error('This email is already used by another customer');

        if (customer.auth_id) {
          const { error: authErr } = await supabase.auth.admin.updateUserById(customer.auth_id, {
            email: value.trim().toLowerCase(),
          });
          if (authErr) throw authErr;
        }
        // Update customers table too
        await supabase.from('customers').update({ email: value.trim().toLowerCase() }).eq('id', customer.id);
        showToast(`Email changed to ${value}`);
      }

      else if (action === 'changePhone') {
        const cleanPhone = value.trim().replace(/\s+/g, '');
        if (cleanPhone) {
          const { data: dup } = await supabase.from('customers').select('id').eq('phone', cleanPhone).neq('id', customer.id).limit(1);
          if (dup?.length > 0) throw new Error('This phone is already used by another customer');
        }
        await supabase.from('customers').update({
          phone: cleanPhone || null,
          whatsapp: cleanPhone || null,
        }).eq('id', customer.id);
        showToast(`Phone updated for ${customer.name}`);
      }

      else if (action === 'suspendAccount') {
        await supabase.from('customers').update({ status: 'suspended' }).eq('id', customer.id);
        showToast(`${customer.name} suspended`);
      }

      else if (action === 'activateAccount') {
        await supabase.from('customers').update({ status: 'active' }).eq('id', customer.id);
        showToast(`${customer.name} activated`);
      }

      onDone();
      onClose();
    } catch (e) {
      setError(e.message || 'Something went wrong');
    }
    setSaving(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ width: '95%', maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>{titles[action]}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: '#94a3b8', cursor: 'pointer' }}>&times;</button>
        </div>

        {/* Customer info */}
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10,
          padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 40, height: 40, borderRadius: 10,
            background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 16, fontWeight: 800, flexShrink: 0,
          }}>
            {customer.name?.charAt(0)?.toUpperCase() || '?'}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{customer.name}</div>
            <div style={{ fontSize: 12, color: '#64748b' }}>{customer.email}</div>
          </div>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2', color: '#dc2626', padding: '8px 12px',
            borderRadius: 8, fontSize: 13, marginBottom: 14,
          }}>{error}</div>
        )}

        {/* Reset Password */}
        {action === 'resetPassword' && !resetDone && (
          <>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                New Password
              </label>
              <input type="text" value={value} onChange={e => setValue(e.target.value)}
                placeholder="Minimum 6 characters" autoFocus
                style={{ fontFamily: 'monospace' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
                Confirm Password
              </label>
              <input type="text" value={confirm} onChange={e => setConfirm(e.target.value)}
                placeholder="Type password again"
                style={{ fontFamily: 'monospace' }} />
            </div>
            <button className="btn-sm" onClick={() => {
              const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
              let pw = '';
              for (let i = 0; i < 8; i++) pw += chars[Math.floor(Math.random() * chars.length)];
              setValue(pw); setConfirm(pw);
            }} style={{ marginBottom: 16, background: '#f0f9ff', color: '#0ea5e9', width: '100%' }}>
              Generate Random Password
            </button>
            {value && value.length >= 6 && value === confirm && (
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8,
                padding: '10px 14px', marginBottom: 14, textAlign: 'center',
              }}>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>New password will be:</div>
                <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'monospace', color: '#166534', letterSpacing: '0.1em' }}>
                  {value}
                </div>
              </div>
            )}
          </>
        )}

        {/* Password Reset Success — Send to customer */}
        {action === 'resetPassword' && resetDone && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>✅</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#166534', marginBottom: 4 }}>
              Password Reset Successful!
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
              New password for {customer.name}:
            </div>

            <div style={{
              background: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: 12,
              padding: '16px 20px', marginBottom: 20,
            }}>
              <div style={{
                fontSize: 28, fontWeight: 800, fontFamily: 'monospace',
                color: '#166534', letterSpacing: '0.15em',
              }}>
                {value}
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 10 }}>
              Send new password to customer:
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {/* Send via Email */}
              <a href={buildEmailLink(customer.email, customer.name, value)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  padding: '14px 20px', borderRadius: 10, textDecoration: 'none',
                  background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff',
                  fontWeight: 700, fontSize: 14, cursor: 'pointer',
                }}>
                ✉️ Send via Email
                <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8 }}>
                  (opens your email app)
                </span>
              </a>

              {/* Send via WhatsApp */}
              {(customer.phone || customer.whatsapp) && (
                <a href={buildWhatsAppLink(customer.phone || customer.whatsapp, customer.name, value)}
                  target="_blank" rel="noreferrer"
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '14px 20px', borderRadius: 10, textDecoration: 'none',
                    background: '#25D366', color: '#fff',
                    fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  }}>
                  💬 Send via WhatsApp
                </a>
              )}

              {/* Copy password */}
              <button className="btn-sm" onClick={() => {
                navigator.clipboard.writeText(value);
                showToast('Password copied to clipboard!');
              }} style={{ width: '100%', background: '#f1f5f9', color: '#475569', padding: '10px' }}>
                📋 Copy Password
              </button>
            </div>

            <div style={{ fontSize: 11, color: '#94a3b8', lineHeight: 1.5 }}>
              Email will open with your configured email template.
              <br />Configure templates in Admin Settings.
            </div>
          </div>
        )}

        {/* Change Email */}
        {action === 'changeEmail' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Current: <span style={{ color: '#64748b', fontWeight: 400 }}>{customer.email}</span>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4, marginTop: 12 }}>
              New Email
            </label>
            <input type="email" value={value} onChange={e => setValue(e.target.value)}
              placeholder="new@email.com" autoFocus />
          </div>
        )}

        {/* Change Phone */}
        {action === 'changePhone' && (
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>
              Current: <span style={{ color: '#64748b', fontWeight: 400 }}>{customer.phone || customer.whatsapp || 'Not set'}</span>
            </label>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4, marginTop: 12 }}>
              New Phone
            </label>
            <input type="tel" value={value} onChange={e => setValue(e.target.value)}
              placeholder="+91 98765 43210" autoFocus />
          </div>
        )}

        {/* Suspend / Activate */}
        {(action === 'suspendAccount' || action === 'activateAccount') && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>
              {action === 'suspendAccount'
                ? `This will suspend ${customer.name}'s account. They won't be able to access the dashboard.`
                : `This will reactivate ${customer.name}'s account. They'll be able to log in again.`}
            </p>
          </div>
        )}

        {/* Actions */}
        {resetDone ? (
          <button className="btn-sm" onClick={() => { onDone(); onClose(); }}
            style={{ width: '100%', marginTop: 8 }}>
            Done
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn-sm" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
            <button className="btn" onClick={handleSave} disabled={saving} style={{
              flex: 1, marginTop: 0,
              background: action === 'suspendAccount' ? '#ef4444'
                : action === 'activateAccount' ? '#22c55e'
                : undefined,
            }}>
              {saving ? 'Saving...'
                : action === 'resetPassword' ? 'Reset Password'
                : action === 'changeEmail' ? 'Change Email'
                : action === 'changePhone' ? 'Update Phone'
                : action === 'suspendAccount' ? 'Suspend'
                : action === 'activateAccount' ? 'Activate'
                : 'Save'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Main Customers Component
   ═══════════════════════════════════════════════════════════════════════════ */
export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [kidsMap,   setKidsMap]   = useState({});
  const [devicesMap, setDevicesMap] = useState({});
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');
  const [planFilter,setPlanFilter] = useState('all');
  const [modal,     setModal]     = useState(null);
  const [form,      setForm]      = useState(EMPTY);
  const [formKids,  setFormKids]  = useState([{ kid_name: '', kid_username: '' }]);
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState('');
  const [confirm,   setConfirm]   = useState(null);
  const [expanded,  setExpanded]  = useState(null);
  const [togglingKid, setTogglingKid] = useState(null);
  const [generatingCode, setGeneratingCode] = useState(null);
  const [generatedCodes, setGeneratedCodes] = useState({});

  // Action modal state
  const [actionModal, setActionModal] = useState(null); // { action, customer }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: custData }, { data: kidsData }, { data: devicesData }] = await Promise.all([
      supabase.from('customers').select('*').order('created_at', { ascending: false }),
      supabase.from('kids').select('*').order('created_at', { ascending: true }),
      supabase.from('devices').select('*'),
    ]);
    setCustomers(custData || []);

    const map = {};
    (kidsData || []).forEach(k => {
      if (!map[k.customer_id]) map[k.customer_id] = [];
      map[k.customer_id].push(k);
    });
    setKidsMap(map);

    const dMap = {};
    (devicesData || []).forEach(d => {
      if (d.kid_id) dMap[d.kid_id] = d;
    });
    setDevicesMap(dMap);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setForm(EMPTY);
    setFormKids([{ kid_name: '', kid_username: '' }]);
    setModal({ mode: 'add' });
  };

  const openEdit = (c) => {
    setForm({
      ...c,
      phone: c.phone || c.whatsapp || '',
      trial_ends_at: c.trial_ends_at
        ? new Date(c.trial_ends_at).toISOString().slice(0, 16)
        : EMPTY.trial_ends_at,
    });
    setFormKids(
      (kidsMap[c.id] || []).map(k => ({ id: k.id, kid_name: k.kid_name, kid_username: k.kid_username }))
    );
    if (!kidsMap[c.id]?.length) setFormKids([{ kid_name: '', kid_username: '' }]);
    setModal({ mode: 'edit', id: c.id });
  };

  const save = async () => {
    if (!form.name.trim()) return showToast('Name is required');
    setSaving(true);

    const validKids = formKids.filter(k => k.kid_name.trim() && k.kid_username.trim());

    const payload = {
      name: form.name, email: form.email, whatsapp: form.whatsapp || form.phone,
      phone: form.phone, plan: form.plan, device_name: form.device_name,
      status: form.status, notes: form.notes, num_kids: validKids.length,
      trial_ends_at: form.trial_ends_at ? new Date(form.trial_ends_at).toISOString() : null,
    };

    try {
      let customerId;
      if (modal.mode === 'add') {
        if (form.email) {
          const { data: dup } = await supabase.from('customers').select('id').eq('email', form.email.trim().toLowerCase()).limit(1);
          if (dup?.length > 0) throw new Error('A customer with this email already exists.');
        }
        if (form.phone?.trim()) {
          const { data: dup } = await supabase.from('customers').select('id').eq('phone', form.phone.trim()).limit(1);
          if (dup?.length > 0) throw new Error('A customer with this phone already exists.');
        }
        const { data, error } = await supabase.from('customers').insert(payload).select();
        if (error) throw error;
        customerId = data[0].id;
        showToast('Customer added');
      } else {
        customerId = modal.id;
        const { error } = await supabase.from('customers').update(payload).eq('id', customerId);
        if (error) throw error;
        showToast('Customer updated');
      }

      if (modal.mode === 'edit') {
        await supabase.from('kids').delete().eq('customer_id', customerId);
      }
      if (validKids.length > 0) {
        const kidInserts = validKids.map(k => ({
          customer_id: customerId,
          kid_name: k.kid_name.trim(),
          kid_username: k.kid_username.trim().toLowerCase(),
        }));
        const { error: kidErr } = await supabase.from('kids').insert(kidInserts);
        if (kidErr) throw kidErr;
      }
    } catch (e) {
      showToast('Error: ' + e.message);
    }

    setSaving(false);
    setModal(null);
    load();
  };

  const deleteCust = async (id) => {
    const cust = customers.find(c => c.id === id);
    // Delete kids, settings, and customer
    await supabase.from('kids').delete().eq('customer_id', id);
    if (cust?.auth_id) {
      await supabase.from('settings').delete().eq('parent_id', cust.auth_id);
      // Also delete from Supabase Auth
      try { await supabase.auth.admin.deleteUser(cust.auth_id); } catch (e) { /* ignore */ }
    }
    await supabase.from('customers').delete().eq('id', id);
    setCustomers(prev => prev.filter(c => c.id !== id));
    showToast('Customer deleted');
    setConfirm(null);
  };

  const changePlan = async (id, plan) => {
    await supabase.from('customers').update({ plan }).eq('id', id);
    const cust = customers.find(c => c.id === id);
    if (cust?.auth_id) {
      const updates = { plan };
      if (plan === 'trial') {
        updates.trial_ends_at = new Date(Date.now() + 7 * 86400000).toISOString();
      }
      await supabase.from('settings').update(updates).eq('parent_id', cust.auth_id);
    }
    setCustomers(prev => prev.map(c => c.id === id ? { ...c, plan } : c));
    showToast(`Plan changed to ${plan}`);
  };

  const toggleKidActive = async (kid) => {
    setTogglingKid(kid.id);
    try {
      const newVal = !kid.is_active;
      const { error } = await supabase.from('kids').update({ is_active: newVal }).eq('id', kid.id);
      if (error) throw error;
      setKidsMap(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(custId => {
          updated[custId] = updated[custId].map(k =>
            k.id === kid.id ? { ...k, is_active: newVal } : k
          );
        });
        return updated;
      });
      showToast(`${kid.kid_name} ${newVal ? 'activated' : 'paused'}`);
    } catch (e) {
      showToast('Error: ' + e.message);
    }
    setTogglingKid(null);
  };

  const generateSetupCode = async (kid, customer) => {
    setGeneratingCode(kid.id);
    try {
      const code = String(Math.floor(100000 + Math.random() * 900000));
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString();
      const { error } = await supabase.from('devices').insert({
        kid_id: kid.id,
        parent_id: customer.auth_id,
        setup_code: code,
        setup_code_expires_at: expiresAt,
        device_name: `${kid.kid_name}'s device (pending)`,
      });
      if (error) throw error;
      setGeneratedCodes(prev => ({ ...prev, [kid.id]: code }));
      showToast(`Setup code generated: ${code}`);
    } catch (e) {
      showToast('Error: ' + e.message);
    }
    setGeneratingCode(null);
  };

  const displayed = customers.filter(c => {
    const q = search.toLowerCase();
    const match = !q || c.name?.toLowerCase().includes(q)
      || c.email?.toLowerCase().includes(q)
      || c.phone?.includes(q)
      || c.whatsapp?.includes(q);
    const planOk = planFilter === 'all' || c.plan === planFilter;
    return match && planOk;
  });

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <h1 className="page-title" style={{ marginBottom: 0 }}>
          Customers
          <span style={{ fontSize: 14, color: '#64748b', fontWeight: 400, marginLeft: 8 }}>({customers.length})</span>
        </h1>
        <button className="btn" style={{ marginTop: 0 }} onClick={openAdd}>+ Add Customer</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search name, email, phone..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200, maxWidth: 340 }} />
        <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} style={{ width: 'auto' }}>
          <option value="all">All plans</option>
          {PLAN_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, color: '#64748b', fontSize: 13 }}>Loading...</div>
        ) : displayed.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>--</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>No customers yet</div>
            <div style={{ fontSize: 13 }}>Click "+ Add Customer" to get started</div>
          </div>
        ) : (
          <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Customer</th>
                <th>Phone</th>
                <th>Plan</th>
                <th>Kids</th>
                <th>Status</th>
                <th>Trial</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(c => {
                const kids = kidsMap[c.id] || [];
                const isExpanded = expanded === c.id;
                return (
                  <React.Fragment key={c.id}>
                    <tr style={{ cursor: 'pointer' }} onClick={() => setExpanded(isExpanded ? null : c.id)}>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</div>
                        {c.email && <div style={{ fontSize: 11, color: '#64748b' }}>{c.email}</div>}
                      </td>
                      <td style={{ fontSize: 12, color: '#64748b' }}>
                        {c.phone || c.whatsapp ? (
                          <a href={`https://wa.me/${(c.phone || c.whatsapp).replace(/\D/g,'')}`}
                            target="_blank" rel="noreferrer"
                            style={{ color: '#22c55e', textDecoration: 'none', fontSize: 12 }}
                            onClick={e => e.stopPropagation()}>
                            {c.phone || c.whatsapp}
                          </a>
                        ) : '\u2014'}
                      </td>
                      <td>
                        <select value={c.plan}
                          onClick={e => e.stopPropagation()}
                          onChange={e => changePlan(c.id, e.target.value)}
                          style={{ width: 'auto', padding: '4px 8px', fontSize: 12, border: '1px solid #e2e8f0' }}>
                          {PLAN_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </td>
                      <td>
                        {kids.length > 0 ? (
                          <span className="badge badge-blue" style={{ cursor: 'pointer' }}>
                            {kids.length} kid{kids.length > 1 ? 's' : ''}
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#94a3b8' }}>{'\u2014'}</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${
                          c.status === 'active' ? 'badge-green' :
                          c.status === 'suspended' ? 'badge-yellow' : 'badge-red'
                        }`}>{c.status}</span>
                      </td>
                      <td><TrialBadge customer={c} /></td>
                      <td style={{ fontSize: 11, color: '#94a3b8', whiteSpace: 'nowrap' }}>
                        {format(new Date(c.created_at), 'dd MMM yyyy')}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
                          <button className="btn-sm" onClick={() => openEdit(c)} title="Edit profile">Edit</button>
                          <button className="btn-sm" style={{ background: '#fef3c7', color: '#d97706' }}
                            onClick={() => setActionModal({ action: 'resetPassword', customer: c })} title="Reset password">
                            {'\uD83D\uDD11'}
                          </button>
                          <button className="btn-sm" style={{ background: '#f0f9ff', color: '#0ea5e9' }}
                            onClick={() => setActionModal({ action: 'changeEmail', customer: c })} title="Change email">
                            {'\u2709\uFE0F'}
                          </button>
                          <button className="btn-sm" style={{ background: '#f0fdf4', color: '#16a34a' }}
                            onClick={() => setActionModal({ action: 'changePhone', customer: c })} title="Change phone">
                            {'\uD83D\uDCF1'}
                          </button>
                          {c.status === 'active' ? (
                            <button className="btn-sm" style={{ background: '#fef9c3', color: '#ca8a04' }}
                              onClick={() => setActionModal({ action: 'suspendAccount', customer: c })} title="Suspend">
                              {'\u23F8\uFE0F'}
                            </button>
                          ) : (
                            <button className="btn-sm" style={{ background: '#dcfce7', color: '#16a34a' }}
                              onClick={() => setActionModal({ action: 'activateAccount', customer: c })} title="Activate">
                              {'\u25B6\uFE0F'}
                            </button>
                          )}
                          <button className="btn-sm" style={{ background: '#fee2e2', color: '#dc2626' }}
                            onClick={() => setConfirm(c.id)} title="Delete">
                            {'\uD83D\uDDD1\uFE0F'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {/* Expanded kids row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={8} style={{ background: '#f8fafc', padding: '12px 24px' }}>
                          {kids.length > 0 ? (
                            <>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Children ({kids.length})
                              </div>
                              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                {kids.map(k => {
                                  const device = devicesMap[k.id];
                                  const isOnline = device?.last_seen_at &&
                                    differenceInMinutes(new Date(), new Date(device.last_seen_at)) < 5;
                                  const isActive = k.is_active !== false;
                                  return (
                                    <div key={k.id} style={{
                                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                                      padding: '10px 14px', minWidth: 220,
                                    }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                        <div style={{ flex: 1 }}>
                                          <div style={{ fontWeight: 600, fontSize: 13 }}>{k.kid_name}</div>
                                          <div style={{ fontSize: 12, color: '#0ea5e9', fontFamily: 'monospace' }}>@{k.kid_username}</div>
                                        </div>
                                        <span className={`badge ${isActive ? 'badge-green' : 'badge-yellow'}`}
                                          style={{ fontSize: 10 }}>
                                          {isActive ? 'Active' : 'Paused'}
                                        </span>
                                      </div>
                                      {device && (
                                        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                          <div style={{ width: 7, height: 7, borderRadius: '50%', background: isOnline ? '#22c55e' : '#94a3b8' }} />
                                          <span>{device.device_name}</span>
                                          <span style={{ fontSize: 10, color: isOnline ? '#22c55e' : '#94a3b8' }}>({isOnline ? 'Online' : 'Offline'})</span>
                                        </div>
                                      )}
                                      {!device && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>No device linked</div>}
                                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                                        <button className="btn-sm" style={{ fontSize: 11, background: isActive ? '#fef3c7' : '#dcfce7', color: isActive ? '#d97706' : '#16a34a' }}
                                          disabled={togglingKid === k.id}
                                          onClick={(e) => { e.stopPropagation(); toggleKidActive(k); }}>
                                          {togglingKid === k.id ? '...' : isActive ? 'Pause' : 'Activate'}
                                        </button>
                                        <button className="btn-sm" style={{ fontSize: 11, background: '#f0f9ff', color: '#0ea5e9' }}
                                          disabled={generatingCode === k.id}
                                          onClick={(e) => { e.stopPropagation(); generateSetupCode(k, c); }}>
                                          {generatingCode === k.id ? '...' : 'Generate Code'}
                                        </button>
                                      </div>
                                      {generatedCodes[k.id] && (
                                        <div style={{
                                          marginTop: 6, padding: '6px 10px', background: '#f0fdf4',
                                          borderRadius: 6, border: '1px solid #bbf7d0',
                                          fontSize: 13, fontFamily: 'monospace', fontWeight: 700,
                                          color: '#16a34a', letterSpacing: '0.15em', textAlign: 'center',
                                        }}>
                                          Code: {generatedCodes[k.id]}
                                          <div style={{ fontSize: 10, fontWeight: 400, color: '#64748b', letterSpacing: 'normal', marginTop: 2 }}>
                                            Expires in 15 min
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </>
                          ) : (
                            <div style={{ fontSize: 13, color: '#94a3b8' }}>No children added yet</div>
                          )}
                          {/* Account info footer */}
                          <div style={{ marginTop: 12, padding: '10px 0', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                            {c.auth_id && (
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                Auth ID: <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{c.auth_id.slice(0, 8)}...</span>
                              </div>
                            )}
                            {c.device_name && (
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                Device: <strong style={{ color: '#64748b' }}>{c.device_name}</strong>
                              </div>
                            )}
                            {c.notes && (
                              <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
                                Notes: {c.notes}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {modal && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: '95%', maxWidth: 560 }}>
            <div className="modal-title">{modal.mode === 'add' ? '+ Add Customer' : 'Edit Customer'}</div>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Parent</div>
            <div className="modal-grid">
              <div><label>Full Name *</label><input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. Priya Sharma" /></div>
              <div><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="parent@email.com" /></div>
              <div><label>Phone / WhatsApp</label><input value={form.phone} onChange={e => setForm(f => ({...f, phone: e.target.value}))} placeholder="+91 98765 43210" /></div>
              <div>
                <label>Plan</label>
                <select value={form.plan} onChange={e => setForm(f => ({...f, plan: e.target.value}))}>
                  {PLAN_OPTS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label>Status</label>
                <select value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                  {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div><label>Device Name</label><input value={form.device_name} onChange={e => setForm(f => ({...f, device_name: e.target.value}))} placeholder="e.g. Aryan's MacBook" /></div>
              {form.plan === 'trial' && (
                <div>
                  <label>Trial Ends At</label>
                  <input type="datetime-local" value={form.trial_ends_at}
                    onChange={e => setForm(f => ({...f, trial_ends_at: e.target.value}))} />
                </div>
              )}
            </div>

            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 20, marginBottom: 8 }}>
              Children ({formKids.filter(k => k.kid_name.trim()).length})
            </div>
            {formKids.map((kid, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-end' }}>
                <div style={{ flex: 1 }}>
                  {i === 0 && <label style={{ fontSize: 11 }}>Name</label>}
                  <input value={kid.kid_name} placeholder="Child's name"
                    onChange={e => {
                      const arr = [...formKids];
                      arr[i] = { ...arr[i], kid_name: e.target.value };
                      setFormKids(arr);
                    }} />
                </div>
                <div style={{ flex: 1 }}>
                  {i === 0 && <label style={{ fontSize: 11 }}>Username</label>}
                  <input value={kid.kid_username} placeholder="unique_username"
                    onChange={e => {
                      const val = e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, '').slice(0, 20);
                      const arr = [...formKids];
                      arr[i] = { ...arr[i], kid_username: val };
                      setFormKids(arr);
                    }} />
                </div>
                {formKids.length > 1 && (
                  <button className="btn-sm" style={{ background: '#fee2e2', color: '#dc2626', marginBottom: 2 }}
                    onClick={() => setFormKids(prev => prev.filter((_, j) => j !== i))}>X</button>
                )}
              </div>
            ))}
            <button className="btn-sm" style={{ width: '100%', marginTop: 4, background: '#f0f9ff', color: '#0ea5e9', border: '1.5px dashed #bae6fd' }}
              onClick={() => setFormKids(prev => [...prev, { kid_name: '', kid_username: '' }])}>
              + Add child
            </button>

            <div style={{ marginTop: 16, marginBottom: 20 }}>
              <label>Notes (private)</label>
              <textarea rows={2} value={form.notes}
                onChange={e => setForm(f => ({...f, notes: e.target.value}))}
                placeholder="Any private notes..." />
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-sm" onClick={() => setModal(null)}>Cancel</button>
              <button className="btn" style={{ marginTop: 0 }} onClick={save} disabled={saving}>
                {saving ? 'Saving...' : modal.mode === 'add' ? 'Add Customer' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Action Modal (Reset Password / Change Email / etc) */}
      {actionModal && (
        <ActionModal
          action={actionModal.action}
          customer={actionModal.customer}
          onClose={() => setActionModal(null)}
          onDone={load}
          showToast={showToast}
        />
      )}

      {/* Confirm delete */}
      {confirm && (
        <div className="modal-overlay">
          <div className="modal" style={{ width: '92%', maxWidth: 380, textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>{'\u26A0\uFE0F'}</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>Delete customer?</div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
              This will permanently delete:
            </div>
            <div style={{
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
              padding: '10px 14px', marginBottom: 20, textAlign: 'left', fontSize: 12, color: '#991b1b', lineHeight: 1.6,
            }}>
              {'\u2022'} Customer account & auth login<br/>
              {'\u2022'} All children profiles<br/>
              {'\u2022'} All settings & preferences<br/>
              {'\u2022'} This cannot be undone
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button className="btn-sm" onClick={() => setConfirm(null)}>Cancel</button>
              <button className="btn btn-red" style={{ marginTop: 0 }} onClick={() => deleteCust(confirm)}>Delete Forever</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
